import { useState, useEffect, useRef, useCallback } from 'react';
import { useUpdateExpenseItemMutation } from '../../../../__generated__/graphql';
import {
  geocodeAddressHelper,
  buildAddressUpdateInput,
  ADDRESS_UPDATE_DEBOUNCE_DELAY,
  GEOCODE_DEBOUNCE_DELAY,
} from '../carMileageHelpers';
import { Coordinates } from '../../../../../../components/GoogleMapsRoute';

type UseCarMileageAddressesProps = {
  travelExpenseItemId: string | null;
  expenseId: string | undefined;
  hasLoadedInitialData: boolean;
  initialAddressFrom?: string | null | undefined;
  initialAddressTo?: string | null | undefined;
};

export const useCarMileageAddresses = ({
  travelExpenseItemId,
  expenseId,
  hasLoadedInitialData,
  initialAddressFrom = '',
  initialAddressTo = '',
}: UseCarMileageAddressesProps) => {
  const [addressFrom, setAddressFrom] = useState(initialAddressFrom ?? '');
  const [addressTo, setAddressTo] = useState(initialAddressTo ?? '');
  const [startCoords, setStartCoords] = useState<Coordinates | null>(null);
  const [endCoords, setEndCoords] = useState<Coordinates | null>(null);
  const [isUpdatingExpenseItem, setIsUpdatingExpenseItem] = useState(false);

  const [updateExpenseItemMutation] = useUpdateExpenseItemMutation();
  const addressUpdateTimerRef = useRef<NodeJS.Timeout | null>(null);

  const geocodeAddress = useCallback(
    (address: string, type: 'start' | 'end') => {
      geocodeAddressHelper(address, type, {
        onSuccess: (coords, formattedAddress, addressType) => {
          if (addressType === 'start') {
            setStartCoords(coords);
            setAddressFrom(formattedAddress);
          } else {
            setEndCoords(coords);
            setAddressTo(formattedAddress);
          }
        },
        onError: (err) => {
          console.error('Geocoding failed:', err);
        },
      });
    },
    [],
  );

  useEffect(() => {
    if (!hasLoadedInitialData || !travelExpenseItemId || !expenseId) {
      return;
    }

    if (addressUpdateTimerRef.current) {
      clearTimeout(addressUpdateTimerRef.current);
    }

    addressUpdateTimerRef.current = setTimeout(async () => {
      try {
        setIsUpdatingExpenseItem(true);
        await updateExpenseItemMutation({
          variables: {
            input: buildAddressUpdateInput(
              travelExpenseItemId,
              addressFrom,
              addressTo,
            ),
          },
        });
      } catch (err) {
        console.error('Error updating expense item addresses:', err);
      } finally {
        setIsUpdatingExpenseItem(false);
      }
    }, ADDRESS_UPDATE_DEBOUNCE_DELAY);

    return () => {
      if (addressUpdateTimerRef.current) {
        clearTimeout(addressUpdateTimerRef.current);
      }
    };
  }, [
    addressFrom,
    addressTo,
    travelExpenseItemId,
    hasLoadedInitialData,
    expenseId,
    updateExpenseItemMutation,
  ]);

  useEffect(() => {
    if (!addressFrom || !hasLoadedInitialData) return;

    const timer = setTimeout(() => {
      geocodeAddress(addressFrom, 'start');
    }, GEOCODE_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [addressFrom, hasLoadedInitialData, geocodeAddress]);

  useEffect(() => {
    if (!addressTo || !hasLoadedInitialData) return;

    const timer = setTimeout(() => {
      geocodeAddress(addressTo, 'end');
    }, GEOCODE_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [addressTo, hasLoadedInitialData, geocodeAddress]);

  return {
    addressFrom,
    addressTo,
    startCoords,
    endCoords,
    isUpdatingExpenseItem,
    setAddressFrom,
    setAddressTo,
    geocodeAddress,
  };
};
